import { useState, useEffect, useCallback, useRef } from "react";
import { 
  Globe, 
  Search, 
  Plus, 
  Trash2, 
  RefreshCw, 
  ExternalLink,
  Mail,
  Filter,
  X,
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Sparkles,
  Radar,
  Users,
  CheckSquare,
  Square,
  FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { getWebsites, analyzeWebsite, deleteWebsite, refreshWebsiteEmail, discoverWebsites, createContactList, getContactLists, addWebsitesToList } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import DiscoveryRunPanel from "../components/DiscoveryRunPanel";

// Optional discovery checks. Only on-topic starts ticked (without it most results are
// multi-topic blogs and link sellers); a check that is off doesn't run (no cost)
const DEFAULT_CHECKS = {
  on_topic: true,
  min_traffic_on: false,
  min_traffic: 1000,
  active_on: false,
  active_within_days: 90,
  hide_link_farms: false,
  hide_paid: false,
};

// Defined outside the page component so typing in its inputs doesn't remount it
const CheckOption = ({ id, label, description, checked, onToggle, children }) => (
  <div className="flex items-start gap-3">
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={(e) => onToggle(id, e.target.checked)}
      className="rounded border-border mt-1"
      data-testid={`check-${id}`}
    />
    <div className="flex-1">
      <Label htmlFor={id} className="cursor-pointer">{label}</Label>
      <p className="text-xs text-muted-foreground">{description}</p>
      {checked && children}
    </div>
  </div>
);

const readStoredRunId = () => {
  try {
    return localStorage.getItem("discoveryRunId");
  } catch {
    return null;
  }
};

const storeRunId = (id) => {
  try {
    if (id) localStorage.setItem("discoveryRunId", id);
    else localStorage.removeItem("discoveryRunId");
  } catch {
    // storage unavailable: the panel just won't survive a reload
  }
};

const GuestPostBadge = ({ accepts, evidence }) => {
  if (accepts === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium badge-success" title={evidence || ""}>
        <CheckCircle className="w-3 h-3" />
        Accepts
      </span>
    );
  }
  if (accepts === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium badge-error" title={evidence || ""}>
        <X className="w-3 h-3" />
        No
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-accent text-muted-foreground" title={evidence || ""}>
      <HelpCircle className="w-3 h-3" />
      Unknown
    </span>
  );
};

const QualificationBadge = ({ status, score, flags, rejectionReasons }) => {
  const config = {
    qualified: { class: "badge-success", label: "Qualified", icon: CheckCircle },
    flagged: { class: "badge-warning", label: "Flagged", icon: AlertCircle },
    rejected: { class: "badge-error", label: "Rejected", icon: X },
    pending: { class: "bg-accent text-muted-foreground", label: "Pending", icon: HelpCircle }
  };
  
  const { class: className, label, icon: Icon } = config[status] || config.pending;
  const issues = [...(rejectionReasons || []), ...(flags || [])];
  
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${className}`} title={issues.join(", ")}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
      {score !== null && score !== undefined && (
        <span className={`text-xs font-mono ${score >= 70 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
          {score}
        </span>
      )}
    </div>
  );
};

export default function WebsiteSearch() {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDiscoverDialog, setShowDiscoverDialog] = useState(false);
  const [showCreateListDialog, setShowCreateListDialog] = useState(false);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newNiche, setNewNiche] = useState("");
  const [discoverIndustry, setDiscoverIndustry] = useState("");
  const [discoverMaxResults, setDiscoverMaxResults] = useState(20);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [discoverChecks, setDiscoverChecks] = useState(DEFAULT_CHECKS);
  const [activeRunId, setActiveRunId] = useState(readStoredRunId);
  const lastAcceptedRef = useRef(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [listName, setListName] = useState("");
  const [listDescription, setListDescription] = useState("");
  const [existingLists, setExistingLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [creatingList, setCreatingList] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    minQuality: 0,
    maxQuality: 100,
    hasEmail: null,
    acceptsGuestPosts: null,
    niche: "",
    qualificationStatus: null,
    minQualityScore: null
  });

  const fetchWebsites = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        search: searchQuery || undefined,
        max_da: filters.maxQuality < 100 ? filters.maxQuality : undefined,
        has_email: filters.hasEmail,
        accepts_guest_posts: filters.acceptsGuestPosts,
        niche: filters.niche || undefined,
        qualification_status: filters.qualificationStatus || undefined,
        min_quality_score: filters.minQuality > 0 ? filters.minQuality : (filters.minQualityScore || undefined)
      };
      
      const response = await getWebsites(params);
      setWebsites(response.data);
    } catch (error) {
      toast.error("Failed to fetch websites");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters]);

  useEffect(() => {
    fetchWebsites();
  }, [fetchWebsites]);

  const handleAnalyze = async () => {
    if (!newUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setAnalyzing(true);
    try {
      await analyzeWebsite(newUrl.trim(), newNiche.trim() || undefined);
      toast.success("Website analyzed successfully");
      setNewUrl("");
      setNewNiche("");
      setShowAddDialog(false);
      fetchWebsites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze website");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteWebsite(id);
      toast.success("Website removed");
      fetchWebsites();
    } catch (error) {
      toast.error("Failed to delete website");
    }
  };

  const handleRefreshEmail = async (id) => {
    setRefreshingId(id);
    try {
      const response = await refreshWebsiteEmail(id);
      if (response.data.contact_email) {
        toast.success(`Found email: ${response.data.contact_email}`);
      } else {
        toast.info("No email found");
      }
      fetchWebsites();
    } catch (error) {
      toast.error("Failed to refresh email");
    } finally {
      setRefreshingId(null);
    }
  };

  const resetFilters = () => {
    setFilters({
      minQuality: 0,
      maxQuality: 100,
      hasEmail: null,
      acceptsGuestPosts: null,
      niche: "",
      qualificationStatus: null,
      minQualityScore: null
    });
  };

  const handleDiscover = async () => {
    if (!discoverIndustry.trim()) {
      toast.error("Please enter an industry/niche");
      return;
    }

    setDiscovering(true);
    try {
      const checks = {
        on_topic: discoverChecks.on_topic,
        min_traffic: discoverChecks.min_traffic_on ? Number(discoverChecks.min_traffic) || null : null,
        active_within_days: discoverChecks.active_on ? Number(discoverChecks.active_within_days) || null : null,
        hide_link_farms: discoverChecks.hide_link_farms,
        hide_paid: discoverChecks.hide_paid,
      };
      const response = await discoverWebsites(discoverIndustry.trim(), discoverMaxResults, forceRefresh, checks);
      toast.success(response.data.message);
      lastAcceptedRef.current = -1;
      setActiveRunId(response.data.run_id);
      storeRunId(response.data.run_id);
      setDiscoverIndustry("");
      setForceRefresh(false);
      setShowDiscoverDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to discover websites");
    } finally {
      setDiscovering(false);
    }
  };

  // Refresh the table as a discovery run adds sites
  const handleRunUpdate = (run) => {
    // Polls keep a stale closure, so track the last count in a ref
    if (run.accepted !== lastAcceptedRef.current || run.status !== "running") {
      lastAcceptedRef.current = run.accepted;
      fetchWebsites();
    }
  };

  const toggleCheck = (id, value) => setDiscoverChecks(c => ({ ...c, [id]: value }));

  const closeRunPanel = () => {
    setActiveRunId(null);
    storeRunId(null);
  };

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === websites.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(websites.map(w => w.id)));
    }
  };

  const handleCreateList = async () => {
    if (!listName.trim()) {
      toast.error("Please enter a list name");
      return;
    }
    if (selectedIds.size === 0) {
      toast.error("Please select at least one website");
      return;
    }

    setCreatingList(true);
    try {
      await createContactList({
        name: listName.trim(),
        description: listDescription.trim() || undefined,
        website_ids: Array.from(selectedIds)
      });
      toast.success(`Created list "${listName}" with ${selectedIds.size} contacts`);
      setShowCreateListDialog(false);
      setListName("");
      setListDescription("");
      setSelectedIds(new Set());
    } catch (error) {
      toast.error("Failed to create list");
    } finally {
      setCreatingList(false);
    }
  };

  const handleAddToExistingList = async () => {
    if (!selectedListId) {
      toast.error("Please select a list");
      return;
    }

    try {
      await addWebsitesToList(selectedListId, Array.from(selectedIds));
      toast.success(`Added ${selectedIds.size} contacts to list`);
      setShowAddToListDialog(false);
      setSelectedListId("");
      setSelectedIds(new Set());
    } catch (error) {
      toast.error("Failed to add to list");
    }
  };

  const openAddToListDialog = async () => {
    try {
      const response = await getContactLists();
      setExistingLists(response.data);
      setShowAddToListDialog(true);
    } catch (error) {
      toast.error("Failed to load lists");
    }
  };

  return (
    <div className="space-y-6" data-testid="websites-page">
      {/* Selection Bar */}
      {selectedIds.size > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-primary" />
            <span className="font-medium">{selectedIds.size} websites selected</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openAddToListDialog} data-testid="add-to-list-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add to List
            </Button>
            <Button size="sm" onClick={() => setShowCreateListDialog(true)} data-testid="create-list-btn">
              <Users className="w-4 h-4 mr-1" />
              Create New List
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Websites</h1>
          <p className="text-muted-foreground mt-1">
            Discover and analyze guest blogging opportunities
          </p>
        </div>
        <div className="flex gap-2">
          {/* Discover Dialog */}
          <Dialog open={showDiscoverDialog} onOpenChange={setShowDiscoverDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="discover-btn">
                <Radar className="w-4 h-4 mr-2" />
                Discover Sites
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="discover-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Discover Guest Post Sites
                </DialogTitle>
                <DialogDescription>
                  Automatically find websites accepting guest posts in your industry
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry / Niche *</Label>
                  <Input
                    id="industry"
                    placeholder="e.g., Technology, Health, Finance, Marketing"
                    value={discoverIndustry}
                    onChange={(e) => setDiscoverIndustry(e.target.value)}
                    data-testid="discover-industry-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    We search Google for "write for us" and guest post guideline pages, then confirm each page is real.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Verified sites to collect: {discoverMaxResults}</Label>
                  <Slider
                    value={[discoverMaxResults]}
                    onValueChange={([val]) => setDiscoverMaxResults(val)}
                    min={5}
                    max={50}
                    step={5}
                    data-testid="discover-max-slider"
                  />
                </div>
                <div className="space-y-3 p-3 border border-border rounded-md">
                  <div>
                    <p className="text-sm font-medium">Optional checks</p>
                    <p className="text-xs text-muted-foreground">Untick any you don't want. Unticked checks don't run and cost nothing.</p>
                  </div>
                  <CheckOption id="on_topic" checked={!!discoverChecks.on_topic} onToggle={toggleCheck} label="On-topic only" description="AI reads the homepage and drops sites not about this industry" />
                  <CheckOption id="min_traffic_on" checked={!!discoverChecks.min_traffic_on} onToggle={toggleCheck} label="Minimum traffic" description="Drops sites below a monthly visitor count (DataForSEO, paid per site)">
                    <Input
                      type="number"
                      min={0}
                      className="mt-2 h-8 w-36"
                      value={discoverChecks.min_traffic}
                      onChange={(e) => setDiscoverChecks(c => ({ ...c, min_traffic: e.target.value }))}
                      aria-label="Minimum monthly visits"
                    />
                  </CheckOption>
                  <CheckOption id="active_on" checked={!!discoverChecks.active_on} onToggle={toggleCheck} label="Active blogs only" description="Drops sites with no new post recently (from the site's feed or sitemap)">
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        min={1}
                        className="h-8 w-24"
                        value={discoverChecks.active_within_days}
                        onChange={(e) => setDiscoverChecks(c => ({ ...c, active_within_days: e.target.value }))}
                        aria-label="Days since last post"
                      />
                      <span className="text-xs text-muted-foreground">days since last post</span>
                    </div>
                  </CheckOption>
                  <CheckOption id="hide_link_farms" checked={!!discoverChecks.hide_link_farms} onToggle={toggleCheck} label="Hide link farms" description="Drops sites with spam topics (casino, CBD, loans…) or that accept any niche" />
                  <CheckOption id="hide_paid" checked={!!discoverChecks.hide_paid} onToggle={toggleCheck} label="Hide paid posts" description="Drops sites that list a price for guest posts" />
                </div>
                <div className="flex items-center gap-3 p-3 bg-accent/50 rounded-md">
                  <input
                    type="checkbox"
                    id="force-refresh"
                    checked={forceRefresh}
                    onChange={(e) => setForceRefresh(e.target.checked)}
                    className="rounded border-border"
                    data-testid="force-refresh-checkbox"
                  />
                  <div>
                    <Label htmlFor="force-refresh" className="cursor-pointer">Force fresh search</Label>
                    <p className="text-xs text-muted-foreground">
                      Search again even if this industry was searched in the last 7 days (uses search credits)
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDiscoverDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleDiscover} 
                  disabled={discovering || !!activeRunId}
                  title={activeRunId ? "Close the current discovery panel first" : undefined}
                  data-testid="start-discover-btn"
                >
                  {discovering ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Discovering...
                    </>
                  ) : (
                    <>
                      <Radar className="w-4 h-4 mr-2" />
                      Find Sites
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Website Dialog */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-website-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Website
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="add-website-dialog">
              <DialogHeader>
                <DialogTitle>Analyze Website</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="url">Website URL</Label>
                  <Input
                    id="url"
                    placeholder="example.com"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    data-testid="website-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="niche">Niche (optional)</Label>
                  <Input
                    id="niche"
                    placeholder="e.g., Technology, Health, Finance"
                    value={newNiche}
                    onChange={(e) => setNewNiche(e.target.value)}
                    data-testid="website-niche-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAnalyze} 
                  disabled={analyzing}
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Analyze
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {activeRunId && (
        <DiscoveryRunPanel runId={activeRunId} onUpdate={handleRunUpdate} onClose={closeRunPanel} />
      )}

      {/* Search and Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search domains..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {Object.values(filters).some(v => v !== null && v !== "" && v !== 0 && v !== 100 && v !== 17) && (
                <span className="ml-2 w-2 h-2 rounded-full bg-primary" />
              )}
            </Button>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="pt-4 mt-4 border-t border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-3">
                    <Label>Quality Score: {filters.minQuality} - {filters.maxQuality}</Label>
                    <Slider
                      value={[filters.minQuality, filters.maxQuality]}
                      onValueChange={([min, max]) => setFilters(f => ({ ...f, minQuality: min, maxQuality: max }))}
                      max={100}
                      step={1}
                      data-testid="quality-slider"
                    />
                    <p className="text-xs text-muted-foreground">From DataForSEO traffic, keywords and rankings. Sites without metrics are hidden when a minimum is set.</p>
                  </div>
                  <div className="space-y-3">
                    <Label>Has Email</Label>
                    <Select 
                      value={filters.hasEmail === null ? "all" : filters.hasEmail.toString()}
                      onValueChange={(v) => setFilters(f => ({ ...f, hasEmail: v === "all" ? null : v === "true" }))}
                    >
                      <SelectTrigger data-testid="has-email-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <Label>Accepts Guest Posts</Label>
                    <Select 
                      value={filters.acceptsGuestPosts === null ? "all" : filters.acceptsGuestPosts.toString()}
                      onValueChange={(v) => setFilters(f => ({ ...f, acceptsGuestPosts: v === "all" ? null : v === "true" }))}
                    >
                      <SelectTrigger data-testid="guest-posts-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <Label>Quality Status</Label>
                    <Select 
                      value={filters.qualificationStatus || "all"}
                      onValueChange={(v) => setFilters(f => ({ ...f, qualificationStatus: v === "all" ? null : v }))}
                    >
                      <SelectTrigger data-testid="quality-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="qualified">✓ Qualified Only</SelectItem>
                        <SelectItem value="flagged">⚠ Flagged</SelectItem>
                        <SelectItem value="rejected">✗ Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <Label>Niche</Label>
                    <Input
                      placeholder="Filter by niche..."
                      value={filters.niche}
                      onChange={(e) => setFilters(f => ({ ...f, niche: e.target.value }))}
                      data-testid="niche-filter-input"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters-btn">
                      <X className="w-4 h-4 mr-2" />
                      Reset Filters
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8" data-testid="loading-skeleton">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="skeleton-row">
                  <div className="skeleton-cell flex-1" />
                  <div className="skeleton-cell w-16" />
                  <div className="skeleton-cell w-16" />
                  <div className="skeleton-cell w-16" />
                  <div className="skeleton-cell w-32" />
                </div>
              ))}
            </div>
          ) : websites.length === 0 ? (
            <div className="empty-state" data-testid="empty-state">
              <Globe className="w-16 h-16" strokeWidth={1} />
              <h3 className="text-lg font-semibold mt-4">No websites found</h3>
              <p className="text-sm max-w-sm mt-2">
                Add your first website to analyze its metrics and find contact emails.
              </p>
              <Button 
                className="mt-4" 
                onClick={() => setShowAddDialog(true)}
                data-testid="empty-add-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Website
              </Button>
            </div>
          ) : (
            <table className="data-table" data-testid="websites-table">
              <thead>
                <tr>
                  <th className="w-12">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 hover:bg-accent rounded"
                      data-testid="select-all-checkbox"
                    >
                      {selectedIds.size === websites.length && websites.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-primary" />
                      ) : (
                        <Square className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  </th>
                  <th>Domain</th>
                  <th>Quality</th>
                  <th>Keywords</th>
                  <th>Age</th>
                  <th>Traffic</th>
                  <th>Guest Posts</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {websites.map((website) => (
                  <tr 
                    key={website.id} 
                    data-testid={`website-row-${website.id}`}
                    className={`${selectedIds.has(website.id) ? "bg-primary/5" : ""} ${website.qualification_status === "rejected" ? "opacity-50" : ""}`}
                  >
                    <td>
                      <button
                        onClick={() => toggleSelect(website.id)}
                        className="p-1 hover:bg-accent rounded"
                        data-testid={`checkbox-${website.id}`}
                      >
                        {selectedIds.has(website.id) ? (
                          <CheckSquare className="w-4 h-4 text-primary" />
                        ) : (
                          <Square className="w-4 h-4 text-muted-foreground" />
                        )}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{website.domain}</p>
                          {website.niche && (
                            <p className="text-xs text-muted-foreground">{website.niche}</p>
                          )}
                          {website.flags && website.flags.length > 0 && (
                            <p className="text-xs text-yellow-500 truncate" title={website.flags.join(", ")}>
                              ⚠ {website.flags[0]}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <QualificationBadge 
                        status={website.qualification_status} 
                        score={website.quality_score}
                        flags={website.flags}
                        rejectionReasons={website.rejection_reasons}
                      />
                    </td>
                    <td className="text-sm text-muted-foreground">
                      {website.keywords_count != null ? website.keywords_count.toLocaleString() : "N/A"}
                    </td>
                    <td className="text-sm text-muted-foreground">
                      {website.domain_age_years ? `${website.domain_age_years}y` : "N/A"}
                    </td>
                    <td className="text-sm">
                      <span className={website.traffic_monthly >= 5000 ? "text-green-500" : "text-muted-foreground"}>
                        {website.traffic_monthly ? `${(website.traffic_monthly / 1000).toFixed(0)}k` : website.traffic_estimate || "N/A"}
                      </span>
                    </td>
                    <td><GuestPostBadge accepts={website.accepts_guest_posts} evidence={website.guest_post_evidence} /></td>
                    <td>
                      {website.contact_email ? (
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span
                            className="text-sm font-mono truncate max-w-[150px]"
                            title={website.email_source ? `${website.contact_email} (found on ${website.email_source}${website.email_source_url ? `: ${website.email_source_url}` : ""})` : website.contact_email}
                          >
                            {website.contact_email}
                          </span>
                        </div>
                      ) : website.contact_form_url ? (
                        <a
                          href={website.contact_form_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm hover:underline"
                          title="No email on the site, but it has a contact form"
                        >
                          <FileText className="w-3 h-3" />
                          Contact form
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not found</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => window.open(website.url, "_blank")}
                          title="Visit website"
                          data-testid={`visit-${website.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleRefreshEmail(website.id)}
                          disabled={refreshingId === website.id}
                          title="Refresh email"
                          data-testid={`refresh-${website.id}`}
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshingId === website.id ? "animate-spin" : ""}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(website.id)}
                          title="Delete"
                          data-testid={`delete-${website.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Create List Dialog */}
      <Dialog open={showCreateListDialog} onOpenChange={setShowCreateListDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-list-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Create Contact List
            </DialogTitle>
            <DialogDescription>
              Save {selectedIds.size} selected websites as a contact list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="list-name">List Name *</Label>
              <Input
                id="list-name"
                placeholder="e.g., Tech Blogs Q1 2025"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                data-testid="list-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="list-desc">Description (optional)</Label>
              <Textarea
                id="list-desc"
                placeholder="Notes about this list..."
                value={listDescription}
                onChange={(e) => setListDescription(e.target.value)}
                data-testid="list-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateListDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateList} disabled={creatingList} data-testid="save-list-btn">
              {creatingList ? "Creating..." : "Create List"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Existing List Dialog */}
      <Dialog open={showAddToListDialog} onOpenChange={setShowAddToListDialog}>
        <DialogContent className="sm:max-w-md" data-testid="add-to-list-dialog">
          <DialogHeader>
            <DialogTitle>Add to Existing List</DialogTitle>
            <DialogDescription>
              Add {selectedIds.size} websites to an existing contact list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select List</Label>
              <Select value={selectedListId} onValueChange={setSelectedListId}>
                <SelectTrigger data-testid="select-existing-list">
                  <SelectValue placeholder="Choose a list..." />
                </SelectTrigger>
                <SelectContent>
                  {existingLists.map(list => (
                    <SelectItem key={list.id} value={list.id}>
                      {list.name} ({list.total_contacts} contacts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {existingLists.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No existing lists. Create a new one instead.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToListDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddToExistingList} disabled={!selectedListId} data-testid="add-to-list-confirm-btn">
              Add to List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
